import { useEffect, useState, useRef } from 'react';

const ServicesSection = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Staggered animation
            setTimeout(() => setVisibleItems(prev => [...prev, 0]), 0);
            setTimeout(() => setVisibleItems(prev => [...prev, 1]), 150);
            setTimeout(() => setVisibleItems(prev => [...prev, 2]), 300);
            setTimeout(() => setVisibleItems(prev => [...prev, 3]), 450);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      number: "01",
      title: "Webdesign & Entwicklung",
      description: "Moderne, responsive Websites die Ihre Kunden begeistern. Von der ersten Idee bis zur fertigen Website - alles aus einer Hand.",
      features: [
        "WordPress oder individuelle Lösungen",
        "UX/UI-Design & Markenanpassung", 
        "Hosting & Domain-Einrichtung",
        "Pflege & Wartung inklusive"
      ],
      link: "/webdesign-entwicklung"
    },
    {
      number: "02",
      title: "Social Media Marketing",
      description: "Erreichen Sie Ihre Zielgruppe dort, wo sie sich aufhält. Professionelle Social Media Betreuung für maximale Reichweite und Engagement.",
      features: [
        "Content Creation & Planung",
        "Instagram, Facebook & TikTok",
        "Community Management",
        "Analytics & Performance Tracking"
      ],
      link: "/social-media"
    },
    {
      number: "03",
      title: "Foto & Videographie",
      description: "Professionelle Bilder und Videos, die Ihre Marke zum Leben erwecken. Von Produktfotos bis zu Imagefilmen.",
      features: [
        "Produktfotografie & Lifestyle-Shots",
        "Business-Portraits & Teamfotos",
        "Imagefilme & Werbevideos",
        "Social Media Content & Drohnenaufnahmen"
      ],
      link: "/foto-videographie"
    }
  ];

  const handleServiceClick = (index: number) => {
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);
    }
  };

  const handleNavigate = (link: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.REACT_APP_NAVIGATE(link);
  };

  return (
    <section ref={sectionRef} id="services" className="py-20 lg:py-32 xl:py-40 bg-black">
      <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-7xl">
        
        {/* Header */}
        <div className="mb-16 lg:mb-24 xl:mb-32">
          <div className="flex items-center space-x-3 text-white/40 mb-6 lg:mb-8">
            <div className="w-2 h-2 bg-[#d1bc8a] rounded-full"></div>
            <span className="text-xs uppercase tracking-[0.3em] font-light">Unsere Leistungen</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-bold text-white leading-[0.95] tracking-tight">
            Alles für Ihren<br />
            <span className="text-white/40">digitalen Erfolg</span>
          </h2>
        </div>

        {/* Services List */}
        <div className="space-y-0">
          {services.map((service, index) => (
            <div
              key={index}
              className={`border-t border-white/10 transition-all duration-700 ${
                visibleItems.includes(index) ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
              } ${index === services.length - 1 ? 'border-b' : ''}`}
            >
              <div
                onClick={() => handleServiceClick(index)}
                className="group py-8 lg:py-10 xl:py-12 cursor-pointer transition-all duration-500 hover:bg-white/[0.02]"
              >
                <div className="flex items-start justify-between gap-4 sm:gap-6 lg:gap-8">
                  
                  {/* Left: Number + Title */}
                  <div className="flex items-start gap-4 sm:gap-6 lg:gap-8 flex-1">
                    <span className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white/20 transition-all duration-500 group-hover:text-[#d1bc8a] leading-none pt-1 lg:pt-2">
                      {service.number}
                    </span>
                    <div className="flex-1">
                      <h3 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 lg:mb-4 transition-all duration-500 group-hover:text-[#d1bc8a] leading-tight">
                        {service.title}
                      </h3>
                      
                      {/* Description - always visible on hover or when expanded */}
                      <div className={`overflow-hidden transition-all duration-500 ${
                        expandedIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 group-hover:max-h-96 group-hover:opacity-100'
                      }`}>
                        <p className="text-base lg:text-lg text-white/60 mb-5 lg:mb-6 leading-relaxed">
                          {service.description}
                        </p>
                        
                        {/* Features */}
                        <div className="space-y-2.5 lg:space-y-3 mb-5 lg:mb-6">
                          {service.features.map((feature, featureIndex) => (
                            <div 
                              key={featureIndex}
                              className="flex items-center space-x-2.5 lg:space-x-3 text-white/50"
                            >
                              <div className="w-1.5 h-1.5 bg-[#d1bc8a] rounded-full flex-shrink-0"></div>
                              <span className="text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>

                        {/* Link Button */}
                        <button
                          onClick={(e) => handleNavigate(service.link, e)}
                          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#d1bc8a] transition-all duration-300 group/btn whitespace-nowrap"
                        >
                          <span className="uppercase tracking-wider">Mehr erfahren</span>
                          <i className="ri-arrow-right-line text-lg transition-transform duration-300 group-hover/btn:translate-x-2"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Right: Arrow Icon */}
                  <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center">
                    <i className={`ri-arrow-down-line text-xl lg:text-2xl text-white/40 transition-all duration-500 group-hover:text-[#d1bc8a] ${
                      expandedIndex === index ? 'rotate-180' : 'rotate-0'
                    }`}></i>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default ServicesSection;